import { motion } from 'framer-motion';
import { KeyRound, User, Lock, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { AnimatedInput } from '../AnimatedInput';
import { AnimatedButton } from '../AnimatedButton';
import { springConfig, authThemeConfigs, type AuthTheme } from '../constants';
import type { RegisterFormData } from './types';
import { stepConfig } from './types';

interface AccountSetupStepProps {
  formData: RegisterFormData;
  errors: Record<string, string>;
  isSubmitting: boolean;
  onChange: (field: keyof RegisterFormData, value: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  theme?: AuthTheme;
}

export function AccountSetupStep({
  formData,
  errors,
  isSubmitting,
  onChange,
  onBack,
  onSubmit,
  theme = 'invol',
}: AccountSetupStepProps) {
  const config = stepConfig.account_setup;
  const themeConfig = authThemeConfigs[theme] || authThemeConfigs.invol;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  // Password strength indicator
  const getPasswordStrength = (password: string) => {
    if (!password) return { level: 0, label: '', color: '' };

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 1) return { level: 1, label: 'Weak', color: 'bg-red-500' };
    if (score <= 2) return { level: 2, label: 'Fair', color: 'bg-amber-500' };
    if (score <= 3) return { level: 3, label: 'Good', color: 'bg-blue-500' };
    return { level: 4, label: 'Strong', color: 'bg-emerald-500' };
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const passwordsMatch = formData.password && formData.password_confirm &&
    formData.password === formData.password_confirm;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg ${
            themeConfig.iconPrimaryIsGradient
              ? `bg-gradient-to-br ${themeConfig.iconPrimary} shadow-purple-500/25`
              : `${themeConfig.iconPrimary} ${themeConfig.buttonShadow}`
          }`}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={springConfig.bouncy}
        >
          <KeyRound className={`h-6 w-6 ${themeConfig.iconColor}`} />
        </motion.div>
        <motion.h2
          className="text-2xl font-bold text-slate-900"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {config.title}
        </motion.h2>
        <motion.p
          className="text-slate-500 mt-1"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          {config.subtitle}
        </motion.p>
      </div>

      {/* Submission error */}
      {errors.submit && (
        <motion.div
          initial={{ opacity: 0, y: -10, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          className="mb-6"
        >
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Registration failed
              </p>
              <p className="text-sm text-red-600 mt-0.5">{errors.submit}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Username */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <AnimatedInput
            id="username"
            label="Username"
            placeholder="johndoe"
            icon={<User className="h-4 w-4" />}
            value={formData.username}
            onChange={(e) => onChange('username', e.target.value)}
            error={errors.username}
            autoComplete="username"
            autoFocus
            theme={theme}
          />
        </motion.div>

        {/* Password */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25 }}
        >
          <AnimatedInput
            id="password"
            label="Password"
            type="password"
            placeholder="Choose a strong password"
            showPasswordToggle
            icon={<Lock className="h-4 w-4" />}
            value={formData.password}
            onChange={(e) => onChange('password', e.target.value)}
            error={errors.password}
            autoComplete="new-password"
            theme={theme}
          />

          {/* Password strength indicator */}
          {formData.password && (
            <motion.div
              className="mt-2"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
            >
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full ${passwordStrength.color}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${(passwordStrength.level / 4) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  passwordStrength.level <= 1 ? 'text-red-600' :
                  passwordStrength.level <= 2 ? 'text-amber-600' :
                  passwordStrength.level <= 3 ? 'text-blue-600' :
                  'text-emerald-600'
                }`}>
                  {passwordStrength.label}
                </span>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Confirm password */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <AnimatedInput
            id="password_confirm"
            label="Confirm password"
            type="password"
            placeholder="Re-enter your password"
            showPasswordToggle
            icon={<Lock className="h-4 w-4" />}
            value={formData.password_confirm}
            onChange={(e) => onChange('password_confirm', e.target.value)}
            error={errors.password_confirm}
            autoComplete="new-password"
            theme={theme}
          />

          {/* Password match indicator */}
          {formData.password_confirm && (
            <motion.div
              className="mt-2 flex items-center gap-1.5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {passwordsMatch ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-xs text-emerald-600 font-medium">
                    Passwords match
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                  <span className="text-xs text-red-600 font-medium">
                    Passwords don't match
                  </span>
                </>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Lawyer notice */}
        {formData.user_type === 'lawyer' && (
          <motion.div
            className="p-4 rounded-xl bg-amber-50 border border-amber-100"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Lawyer accounts require verification.
              After registration, your account will be reviewed before you can log in.
            </p>
          </motion.div>
        )}

        {/* Navigation buttons */}
        <motion.div
          className="flex gap-3 pt-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <AnimatedButton
            type="button"
            variant="secondary"
            onClick={onBack}
            disabled={isSubmitting}
            icon={<ArrowLeft className="h-4 w-4" />}
            iconPosition="left"
            theme={theme}
          >
            Back
          </AnimatedButton>
          <AnimatedButton
            type="submit"
            variant="primary"
            size="lg"
            className="flex-1"
            loading={isSubmitting}
            icon={<Check className="h-4 w-4" />}
            theme={theme}
          >
            Create Account
          </AnimatedButton>
        </motion.div>
      </form>
    </motion.div>
  );
}
